import React, {useCallback, useMemo, useState} from "react";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import {Col, Row} from "reactstrap";
import Divider from "@mui/material/Divider";
import Form from "../../base/form";
import Button from "../../base/button/button";
import {deepEqual} from "../../../../core/services/utils/utils";
import Dialog from "../../base/dialog";
import useIsMounted from "../../../hooks/use-is-mounted";
import {MuiRFF, Yup} from '../../form';
import InputAdornment from "@mui/material/InputAdornment";
import {CheckboxForm} from "../../base/checkbox/checkbox";
import Api from "../../../../core/services/api/api";
import {Typography} from "@mui/material";

const formNames = {
    code: 'code',
    name: 'name',
    rate: 'rate',
    isProvincial: 'isProvincial',
}

const UpsertTaxDialog = ({open, setOpen, data, onSuccess, taxes}) => {
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();

    const invalidCodes = useMemo(() => taxes?.map(tax => tax?.code) ?? [], [taxes]);

    const schema = useMemo(() => Yup.object().shape({
        [formNames.code]: Yup.number().nullable().required(ValidateMessages.required).typeError(ValidateMessages.incorrectType("number")).positive(ValidateMessages.greaterThan('0')),
        [formNames.name]: Yup.string().trim().nullable().required(ValidateMessages.required),
        [formNames.rate]: Yup.number().nullable().required(ValidateMessages.required)
            .typeError(ValidateMessages.incorrectType("number"))
            .min(0, ValidateMessages.greaterThan('negative'))
            .max(100, ValidateMessages.lessThan('100')),
        [formNames.isProvincial]: Yup.boolean().nullable().required(ValidateMessages.required).default(true),
    }), []);

    const validate = useMemo(() => makeValidate(schema), [schema]);
    const required = useMemo(() => makeRequired(schema), [schema]);

    const inEditMode = useMemo(() => !!data, [data]);

    const initialValues = useMemo(() => ({
        [formNames.code]: data?.code ?? undefined,
        [formNames.name]: data?.name ?? '',
        [formNames.rate]: data?.rate ?? 0,
        [formNames.isProvincial]: data?.isProvincial ?? false,
    }), [data?.code, data?.isProvincial, data?.name, data?.rate])

    /**
     * Updates the tex.
     * @type {(function(*): Promise<void>)}
     */
    const updateTax = useCallback(async (values) => {
        if (invalidCodes.includes(values?.code) && Number(values?.code) !== data?.code) {
            return {
                [formNames.code]: "The selected code already exists. You may not have duplicated codes in the system.",
            };
        }
        const forApi = {
            id: data?.id,
            oldCode: data?.code,
            code: Number(values?.code),
            name: values?.name,
            rate: Number(values?.rate),
            isProvincial: values?.isProvincial,
            updatedProperties: [
                "Code",
                'Name',
                "Rate",
                "IsProvincial",
            ],
        }
        if (
            forApi.code === data?.code &&
            forApi.name === data?.name &&
            forApi.rate === data?.rate &&
            forApi.isProvincial === data?.isProvincial
        ) {
            setOpen(false);
            return;
        }
        const response = await Api.updateSalesTax(forApi)
        if (!isMounted())
            return
        if (response?.resultFlag) {
            onSuccess({
                id: forApi.id,
                code: forApi.code,
                name: forApi.name,
                rate: forApi.rate,
                isProvincial: forApi.isProvincial,
            })
        }
    }, [data?.code, data?.id, data?.isProvincial, data?.name, data?.rate, invalidCodes, isMounted, onSuccess, setOpen])

    /**
     * Creates a new tax with the provided values.
     * @type {(function(*): Promise<void>)}
     */
    const createTax = useCallback(async (values) => {
        if (invalidCodes.includes(Number(values?.code))) {
            return {
                [formNames.code]: "The selected code already exists. You can not create a duplicated code.",
            };
        }
        const forApi = {
            code: Number(values?.code),
            name: values?.name,
            rate: Number(values?.rate),
            isProvincial: values?.isProvincial,
        }
        const response = await Api.createSalesTax(forApi)
        if (!isMounted())
            return
        if (response?.resultFlag) {
            onSuccess({
                id: response.data?.id,
                code: response.data?.code,
                name: response.data?.name,
                rate: response.data?.rate,
                isProvincial: response.data?.isProvincial,
            })
        }
    }, [invalidCodes, isMounted, onSuccess])

    /**
     * creates a new driver or updates the [selectedDriver] by calling the api regarding this action
     */
    const onSubmit = useCallback(async (values) => {
        setSubmitting(true);
        let errors;
        if (!!inEditMode) {
            errors = await updateTax(values);
        } else {
            errors = await createTax(values);
        }
        if (!isMounted())
            return;
        setSubmitting(false);
        if (!errors) {
            setOpen(false);
        }
        return errors;
    }, [inEditMode, isMounted, updateTax, createTax, setOpen])

    return (
        <>
            <Dialog
                open={open}
                setOpen={setOpen}
                maxWidth={'sm'}
            >
                <Row className='no-gutters align-items-center w-100 transition'>
                    <Row xs={12} className='no-gutters justify-content-center m-3 w-100'>
                        <p className='text-lg text-color-1 font-weight-semi-bold text-center'>
                            {
                                inEditMode
                                    ? 'Edit Selected Tax'
                                    : 'Add a new Tax'
                            }
                        </p>
                    </Row>
                    <Divider className='divider'/>
                    <div className='my-3 w-100'/>
                    <Form
                        className='w-100'
                        onSubmit={onSubmit}
                        validate={validate}
                        initialValues={initialValues}
                        render={({initialValues: formInitialValues, values}) => {
                            return (
                                <Row className='no-gutters justify-content-around w-100'>
                                    <Row className='no-gutters justify-content-around w-85'>
                                        <Col xs={12} className={'mb-3'}>
                                            <MuiRFF.TextField
                                                label={'Name'}
                                                name={formNames.name}
                                                required={required[formNames.name]}
                                            />
                                        </Col>
                                        <Col xs={12} className={'mb-3'}>
                                            <MuiRFF.TextField
                                                label={'Code'}
                                                name={formNames.code}
                                                required={required[formNames.code]}
                                                type={'number'}
                                                InputProps={{
                                                    inputProps: {min: 0},
                                                }}
                                            />
                                        </Col>
                                        <Col xs={12} className={'mb-3'}>
                                            <MuiRFF.TextField
                                                label={'Rate'}
                                                name={formNames.rate}
                                                required={required[formNames.rate]}
                                                type={'number'}
                                                InputProps={{
                                                    inputProps: {min: 0},
                                                    endAdornment: <InputAdornment position={'end'}>%</InputAdornment>
                                                }}
                                            />
                                        </Col>
                                        <Col xs={12}>
                                            <CheckboxForm
                                                name={formNames.isProvincial}
                                                required={required[formNames.isProvincial]}
                                                data={{
                                                    value: values[formNames.isProvincial], label: 'Provincial Tax',
                                                }}
                                            />
                                        </Col>
                                        {
                                            inEditMode && Number(values?.rate) !== data?.rate &&
                                            <Col xs={12} className={'mb-3'}>
                                                <Typography variant={'body2'} className='text-color-3'>
                                                    <Typography
                                                        variant={'body2'}
                                                        color={'secondary.main'}
                                                        component={'span'}
                                                        fontWeight={600}
                                                        sx={{marginInlineEnd: 1}}
                                                    >
                                                        Note:
                                                    </Typography>
                                                    changing the rate of this tax, will affect the related open quotes and sales orders
                                                    in your system. Please do so with caution.
                                                </Typography>
                                            </Col>
                                        }
                                    </Row>
                                    <div className='my-3 w-100'/>
                                    <Divider className='divider'/>
                                    <Row className='no-gutters justify-content-between align-items-start w-100'>
                                        <Button
                                            disabled={!open || submitting}
                                            variant={'dialog'}
                                            color={'primary'}
                                            onClick={() => setOpen(false)}
                                            borderRadius={'0 8px 0 8px'}
                                        >
                                            {submitting ? 'loading...' : 'Close'}
                                        </Button>
                                        <Button
                                            disabled={!open || submitting || (inEditMode && deepEqual(formInitialValues, values))}
                                            variant={'dialog'}
                                            color={'primary'}
                                            type={'submit'}
                                            borderRadius={'8px 0 8px 0'}
                                        >
                                            {submitting ? 'loading...' : inEditMode ? 'Update' : 'Add'}
                                        </Button>
                                    </Row>
                                </Row>
                            );
                        }}
                    />
                </Row>
            </Dialog>
        </>
    );
}

export default UpsertTaxDialog;
